import { parseIso } from '../dates.js';

const SVG = 'http://www.w3.org/2000/svg';

/** Round a raw step to 1, 2, 2.5 or 5 × 10ⁿ. */
function niceStep(range, count) {
    const raw = range / Math.max(1, count);
    const magnitude = 10 ** Math.floor(Math.log10(raw || 1));
    const residual = raw / magnitude;
    const nice = residual > 5 ? 10 : residual > 2.5 ? 5 : residual > 2 ? 2.5 : residual > 1 ? 2 : 1;
    return nice * magnitude;
}

function niceScale(min, max, count = 5) {
    if (min === max) {
        max = min + 1;
    }
    const step = niceStep(max - min, count - 1);
    const lo = Math.floor(min / step) * step;
    const hi = Math.ceil(max / step) * step;
    const ticks = [];
    for (let v = lo; v <= hi + step / 2; v += step) ticks.push(Number(v.toFixed(10)));
    return { min: lo, max: hi, ticks };
}

/** Monotone cubic interpolation (Fritsch–Carlson): smooth curves without overshooting. */
function smoothPath(points) {
    if (points.length < 3) return linearPath(points);

    const n = points.length;
    const dx = [];
    const slope = [];
    for (let i = 0; i < n - 1; i++) {
        dx[i] = points[i + 1][0] - points[i][0];
        slope[i] = (points[i + 1][1] - points[i][1]) / dx[i];
    }

    const tangent = [slope[0]];
    for (let i = 1; i < n - 1; i++) {
        tangent[i] = slope[i - 1] * slope[i] <= 0 ? 0 : (3 * (dx[i - 1] + dx[i])) / ((2 * dx[i] + dx[i - 1]) / slope[i - 1] + (dx[i] + 2 * dx[i - 1]) / slope[i]);
    }
    tangent[n - 1] = slope[n - 2];

    let d = `M${points[0][0]},${points[0][1]}`;
    for (let i = 0; i < n - 1; i++) {
        const [x0, y0] = points[i];
        const [x1, y1] = points[i + 1];
        const h = dx[i] / 3;
        d += `C${x0 + h},${y0 + h * tangent[i]} ${x1 - h},${y1 - h * tangent[i + 1]} ${x1},${y1}`;
    }
    return d;
}

const linearPath = (points) => points.map(([x, y], i) => `${i ? 'L' : 'M'}${x},${y}`).join('');

const colorVar = (color) => (color === 'accent' ? 'var(--mellow-accent)' : color.startsWith('#') || color.startsWith('var(') ? color : `var(--mellow-${color}-500)`);

function el(name, attributes = {}, parent = null) {
    const node = document.createElementNS(SVG, name);
    for (const [key, value] of Object.entries(attributes)) node.setAttribute(key, value);
    parent?.append(node);
    return node;
}

/**
 * <mellow-chart>: line, area and bar charts drawn as SVG from the JSON config rendered by
 * the Blade component. Re-renders on resize and when Livewire updates the data.
 */
export class MellowChart extends HTMLElement {
    connectedCallback() {
        if (this._ready) return this.sync();
        this._ready = true;

        // The container is wire:ignore'd; everything we draw lives inside it.
        const container = this.querySelector('.mellow-chart-canvas');
        if (!container) return;

        // The height comes from the config, not from the container: a page restored from
        // Livewire's navigation cache (browser back) holds our already modified container.
        let height = parseInt(container.style.height, 10);
        try {
            height = JSON.parse(this.querySelector('script[data-chart]')?.textContent ?? '{}').height || height;
        } catch {}

        this.canvas = document.createElement('div');
        this.canvas.className = 'mellow-chart-plot';
        if (height) this.canvas.style.height = `${height}px`;
        container.style.height = '';
        container.replaceChildren(this.canvas);

        if (!this.hasAttribute('data-sparkline')) {
            // Keyboard users explore the data point by point; screen readers also get the data table.
            this.canvas.tabIndex = 0;
            this.canvas.setAttribute('role', 'img');
            this.canvas.setAttribute('aria-label', `${this.getAttribute('aria-label') ?? ''} – ${window.Mellow?.t('chart.explore') ?? ''}`);
        }

        this.canvas.addEventListener('pointermove', this.onPointer);
        this.canvas.addEventListener('pointerleave', () => this.highlight(null));
        this.canvas.addEventListener('keydown', this.onKeydown);
        this.canvas.addEventListener('blur', () => this.highlight(null));

        new ResizeObserver(() => this.render()).observe(this.canvas);
        this.sync();
    }

    /** Re-read the data (e.g. after a Livewire update) and redraw when it changed. */
    sync() {
        const source = this.querySelector('script[data-chart]')?.textContent ?? '{}';
        if (source === this._source) return;
        this._source = source;
        this.config = JSON.parse(source);
        this.render();
    }

    get numbers() {
        const { locale, format } = this.config;
        // Axis labels keep the unit (currency, percent, ms …) but use compact notation.
        const unit = Object.fromEntries(
            ['style', 'currency', 'currencyDisplay', 'unit', 'unitDisplay']
                .filter((key) => format?.[key] !== undefined)
                .map((key) => [key, format[key]]),
        );
        return {
            full: new Intl.NumberFormat(locale, format ?? {}),
            axis: new Intl.NumberFormat(locale, { notation: 'compact', ...unit }),
        };
    }

    /**
     * Format x values: ISO dates become localized short dates. Timestamps with a time
     * ("2026-09-28T08:15:00Z") keep it and are shown in the viewer's time zone.
     */
    formatX(value, long = false) {
        const hasTime = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(value);
        const date = hasTime ? new Date(value) : typeof value === 'string' ? parseIso(value) : null;
        if (!date || Number.isNaN(date.getTime())) return String(value ?? '');

        const fallback = hasTime
            ? (long ? { dateStyle: 'medium', timeStyle: 'short' } : { hour: '2-digit', minute: '2-digit' })
            : (long ? { dateStyle: 'medium' } : { month: 'short', day: 'numeric' });
        return new Intl.DateTimeFormat(this.config.locale, this.config.xFormat ?? fallback).format(date);
    }

    render() {
        if (!this.config || !this.canvas) return;

        const { type, series, data, sparkline, grid } = this.config;
        const width = this.canvas.clientWidth;
        const height = this.canvas.clientHeight;
        if (!width || !height) return;

        const values = data.flatMap((row) => series.map((s) => Number(row[s.key]))).filter(Number.isFinite);
        const scale = niceScale(Math.min(0, ...values), Math.max(0, ...values), sparkline ? 2 : 5);
        const numbers = this.numbers;

        // Leave room for the longest y label.
        const longest = Math.max(...scale.ticks.map((tick) => numbers.axis.format(tick).length));
        const margin = sparkline
            ? { top: 2, right: 2, bottom: 2, left: 2 }
            : { top: 8, right: 8, bottom: 26, left: Math.ceil(longest * 7 + 12) };

        const plot = { width: width - margin.left - margin.right, height: height - margin.top - margin.bottom };
        const y = (value) => margin.top + plot.height - ((value - scale.min) / (scale.max - scale.min)) * plot.height;
        const band = plot.width / Math.max(1, data.length);
        const x = type === 'bar'
            ? (i) => margin.left + band * i + band / 2
            : (i) => margin.left + (data.length > 1 ? (plot.width * i) / (data.length - 1) : plot.width / 2);

        this.geometry = { x, y, margin, plot, band };

        const svg = el('svg', { width, height, viewBox: `0 0 ${width} ${height}`, class: 'mellow-chart-svg', 'aria-hidden': 'true', focusable: 'false' });

        if (grid) {
            const axis = el('g', { class: 'mellow-chart-grid' }, svg);
            for (const tick of scale.ticks) {
                el('line', { x1: margin.left, x2: width - margin.right, y1: y(tick), y2: y(tick) }, axis);
                el('text', { x: margin.left - 8, y: y(tick), 'text-anchor': 'end', 'dominant-baseline': 'middle' }, axis).textContent = numbers.axis.format(tick);
            }
        }

        if (!sparkline) {
            // X labels, thinned out so they don't overlap.
            const labels = el('g', { class: 'mellow-chart-axis' }, svg);
            const labelWidth = Math.max(...data.map((row) => this.formatX(row[this.config.x]).length)) * 7 + 12;
            const stride = Math.max(1, Math.ceil((data.length * labelWidth) / plot.width));
            const last = data.length - 1;
            const shown = data.map((_, i) => i).filter((i) => i % stride === 0);

            // Always label the last point; drop its neighbour when they would collide.
            if (last > 0 && shown.at(-1) !== last) {
                // The last label is end-anchored (extends left), its neighbour centered.
                const gap = x(last) - x(shown.at(-1));
                if (gap >= labelWidth * (type === 'bar' ? 1 : 1.5)) shown.push(last);
                else shown[shown.length - 1] = last;
            }

            shown.forEach((i) => {
                const anchor = type !== 'bar' && i === 0 ? 'start' : type !== 'bar' && i === last ? 'end' : 'middle';
                el('text', { x: x(i), y: height - 6, 'text-anchor': anchor }, labels).textContent = this.formatX(data[i][this.config.x]);
            });
        }

        const defs = el('defs', {}, svg);
        const baseline = y(Math.max(scale.min, Math.min(0, scale.max)));

        if (type === 'bar') {
            const inner = band * 0.7;
            const barWidth = Math.max(1, inner / series.length - 2);
            series.forEach((s, si) => {
                const group = el('g', { fill: colorVar(s.color) }, svg);
                data.forEach((row, i) => {
                    const value = Number(row[s.key]);
                    if (!Number.isFinite(value)) return;
                    const left = x(i) - inner / 2 + si * (barWidth + 2);
                    const top = Math.min(y(value), baseline);
                    el('rect', { x: left, y: top, width: barWidth, height: Math.max(1, Math.abs(baseline - y(value))), rx: Math.min(3, barWidth / 2) }, group);
                });
            });
        } else {
            series.forEach((s, si) => {
                const points = data
                    .map((row, i) => [x(i), y(Number(row[s.key]))])
                    .filter(([, py]) => Number.isFinite(py));
                if (!points.length) return;

                const path = this.config.curve === 'linear' ? linearPath(points) : smoothPath(points);
                const color = colorVar(s.color);

                if (type === 'area') {
                    const id = `${this.id || 'mellow-chart'}-gradient-${si}`;
                    const gradient = el('linearGradient', { id, x1: 0, x2: 0, y1: 0, y2: 1 }, defs);
                    el('stop', { offset: '0%', 'stop-color': color, 'stop-opacity': 0.25 }, gradient);
                    el('stop', { offset: '100%', 'stop-color': color, 'stop-opacity': 0 }, gradient);
                    el('path', { d: `${path}L${points.at(-1)[0]},${baseline}L${points[0][0]},${baseline}Z`, fill: `url(#${id})` }, svg);
                }

                el('path', { d: path, fill: 'none', stroke: color, 'stroke-width': sparkline ? 1.5 : 2, 'stroke-linejoin': 'round', 'stroke-linecap': 'round' }, svg);
            });
        }

        this.cursor = el('g', { class: 'mellow-chart-cursor' }, svg);

        this.canvas.replaceChildren(svg, this.tooltip ??= this.createTooltip(), this.liveRegion ??= this.createLiveRegion());
        this.renderLegend();

        if (this.index !== undefined && this.index !== null) this.highlight(this.index);
    }

    renderLegend() {
        const { legend, series } = this.config;
        let node = this.querySelector('.mellow-chart-legend');

        if (!legend || series.length < 2) {
            node?.remove();
            return;
        }

        if (!node) {
            node = document.createElement('div');
            node.className = 'mellow-chart-legend';
            node.setAttribute('aria-hidden', 'true');
            this.canvas.after(node);
        }

        node.replaceChildren(...series.map((s) => {
            const item = document.createElement('span');
            item.className = 'mellow-chart-legend-item';
            const swatch = document.createElement('span');
            swatch.className = 'mellow-chart-swatch';
            swatch.style.background = colorVar(s.color);
            item.append(swatch, s.label);
            return item;
        }));
    }

    createTooltip() {
        const tooltip = document.createElement('div');
        tooltip.className = 'mellow-chart-tooltip';
        tooltip.hidden = true;
        return tooltip;
    }

    createLiveRegion() {
        const region = document.createElement('div');
        region.className = 'mellow-sr-only';
        region.setAttribute('aria-live', 'polite');
        return region;
    }

    /** Show cursor, point markers and tooltip for one data index (or hide with null). */
    highlight(index) {
        this.index = index;
        const { data, series, type, sparkline } = this.config;
        const { x, y, margin, plot, band } = this.geometry;

        this.cursor.replaceChildren();
        if (index === null || !data[index]) {
            this.tooltip.hidden = true;
            return;
        }

        const row = data[index];
        const cx = x(index);

        if (type === 'bar') {
            el('rect', { x: cx - band / 2, y: margin.top, width: band, height: plot.height, class: 'mellow-chart-band' }, this.cursor);
        } else {
            el('line', { x1: cx, x2: cx, y1: margin.top, y2: margin.top + plot.height, class: 'mellow-chart-cursor-line' }, this.cursor);
            series.forEach((s) => {
                const value = Number(row[s.key]);
                if (Number.isFinite(value)) el('circle', { cx, cy: y(value), r: 4, fill: colorVar(s.color), class: 'mellow-chart-point' }, this.cursor);
            });
        }

        if (sparkline) return;

        const numbers = this.numbers;
        const title = this.formatX(row[this.config.x], true);
        this.tooltip.replaceChildren();
        const heading = document.createElement('div');
        heading.className = 'mellow-chart-tooltip-title';
        heading.textContent = title;
        this.tooltip.append(heading);

        const lines = [];
        for (const s of series) {
            const value = Number(row[s.key]);
            const line = document.createElement('div');
            line.className = 'mellow-chart-tooltip-row';
            const swatch = document.createElement('span');
            swatch.className = 'mellow-chart-swatch';
            swatch.style.background = colorVar(s.color);
            const label = document.createElement('span');
            label.textContent = s.label;
            const number = document.createElement('strong');
            number.textContent = Number.isFinite(value) ? numbers.full.format(value) : '–';
            line.append(swatch, label, number);
            this.tooltip.append(line);
            lines.push(`${s.label}: ${number.textContent}`);
        }

        this.tooltip.hidden = false;
        const width = this.canvas.clientWidth;
        const tipWidth = this.tooltip.offsetWidth;
        const left = cx + 12 + tipWidth > width ? cx - 12 - tipWidth : cx + 12;
        this.tooltip.style.left = `${Math.max(0, left)}px`;
        this.tooltip.style.top = `${margin.top}px`;

        if (this._keyboard) this.liveRegion.textContent = `${title}. ${lines.join(', ')}`;
    }

    onPointer = (event) => {
        if (!this.geometry || !this.config.data.length) return;
        this._keyboard = false;

        const rect = this.canvas.getBoundingClientRect();
        const px = event.clientX - rect.left;
        const { data, type } = this.config;
        const { margin, plot, band } = this.geometry;

        const index = type === 'bar'
            ? Math.floor((px - margin.left) / band)
            : Math.round(((px - margin.left) / plot.width) * (data.length - 1));

        this.highlight(Math.min(data.length - 1, Math.max(0, index)));
    };

    onKeydown = (event) => {
        const last = this.config.data.length - 1;
        const current = this.index ?? -1;
        const rtl = getComputedStyle(this).direction === 'rtl';
        const next = {
            [rtl ? 'ArrowLeft' : 'ArrowRight']: Math.min(last, current + 1),
            [rtl ? 'ArrowRight' : 'ArrowLeft']: Math.max(0, current === -1 ? last : current - 1),
            Home: 0,
            End: last,
        }[event.key];

        if (next !== undefined) {
            event.preventDefault();
            this._keyboard = true;
            this.highlight(next);
        } else if (event.key === 'Escape') {
            this.highlight(null);
        }
    };
}
